import React from "react";
import { Container, Col, Row, Button as PlainButton } from "reactstrap";
import { BreadcrumbItem } from "reactstrap";
import { Component as Form } from "@repoflow/form-schema";
import * as Notify from "@repoflow/form-schema/notify";
import {
  Mutation,
  Button,
  useSectionContext,
} from "@repoflow/components/section";
import { CancellableContent } from "@repoflow/components/content-cancel";
import * as widgets from "widgets/register";
import * as QueriesQL from "queries/blog-collection";
import * as PostSelector from "components/post-selector";
import { useWrite } from "forms/write";

const EDIT_FIELDS = {
  "/Public/BlogCollection/Update": {
    fields: [
      "title",
      { property: "description", layout: "wide" },
      { property: "image", layout: "wide" },
    ],
  },
};

// The membership, moved around: the pure half, so the order rules are one
// function a test can call.
export const moved = (postIds, index, delta) => {
  const next = [...postIds];
  const target = index + delta;

  if (index < 0 || index >= next.length || target < 0 || target >= next.length) {
    return postIds;
  }

  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

export const without = (postIds, postId) => postIds.filter((id) => id !== postId);

export const added = (postIds, postId) =>
  postIds.includes(postId) ? postIds : [...postIds, postId];

export const Sidebar = () => null;
export const Indice = () => (
  <>
    <BreadcrumbItem>Edit</BreadcrumbItem>
  </>
);

export const Content = ({
  history,
  match: {
    params: { siteId, collectionId },
  },
}) => {
  const { base } = useSectionContext();

  return (
    <Mutation
      target={{ $ref: "/Public/BlogCollection/Update" }}
      mutation={QueriesQL.edit}
      update={QueriesQL.update()}
      onMutate={({ handler, state: { values } }) => {
        handler({
          variables: {
            siteId,
            collectionId,
            input: {
              title: values.title,
              description: values.description || "",
              image: values.image || { url: "", description: "" },
              postIds: values.postIds || [],
            },
          },
        });
      }}
      onCompleted={() => {
        history.replace(base);
      }}
      onError={(error) => {
        Notify.error(error.toString());
      }}
    >
      {MutationHandler}
    </Mutation>
  );
};

// The members are edited beside the form rather than inside it: a list of
// ids means nothing on screen, so each row shows the post's title, and the
// picker adds by title too. Both write through one merge over the value the
// form holds (forms/write), which is what keeps the form's own fields intact
// while the list beside them changes.
const Members = ({ postIds, titles, onChange }) => (
  <div className="mt-3" data-members>
    <h6>Posts</h6>
    {postIds.length === 0 && <p className="text-muted">No posts in this collection yet.</p>}
    <ol className="list-group list-group-numbered mb-2">
      {postIds.map((postId, index) => (
        <li key={postId} className="list-group-item d-flex justify-content-between align-items-center">
          <span>{titles[postId] || postId}</span>
          <span>
            <PlainButton
              type="button"
              size="sm"
              color="light"
              aria-label={`Move ${titles[postId] || postId} up`}
              disabled={index === 0}
              onClick={() => onChange(moved(postIds, index, -1))}
            >
              <i className="fa fa-arrow-up" />
            </PlainButton>{" "}
            <PlainButton
              type="button"
              size="sm"
              color="light"
              aria-label={`Move ${titles[postId] || postId} down`}
              disabled={index === postIds.length - 1}
              onClick={() => onChange(moved(postIds, index, 1))}
            >
              <i className="fa fa-arrow-down" />
            </PlainButton>{" "}
            <PlainButton
              type="button"
              size="sm"
              color="danger"
              outline
              aria-label={`Remove ${titles[postId] || postId}`}
              onClick={() => onChange(without(postIds, postId))}
            >
              <i className="fa fa-times" />
            </PlainButton>
          </span>
        </li>
      ))}
    </ol>
    <PostSelector.SelectButton
      chosen={postIds}
      onSelect={(post) => onChange(added(postIds, post.postId), post)}
    />
  </div>
);

const MutationHandler = ({ state, schema, mutate, target }) => {
  const { values, setValues } = state;
  const write = useWrite(values, setValues);
  // The titles of the members, remembered as they are picked. A member picked
  // in an earlier session shows its id until this screen learns its title,
  // which is the one thing a list of ids costs.
  const [titles, setTitles] = React.useState({});
  const postIds = values.postIds || [];

  const formProps = {
    schema,
    target,
    widgets,
    onSubmit: () => {
      mutate();
    },
    onChange: (vals) => {
      write(vals);
    },
    config: EDIT_FIELDS,
    values,
  };

  return (
    <CancellableContent>
      <Container className="mt-4">
        <Row>
          <Col>
            <Form {...formProps}>
              <Members
                postIds={postIds}
                titles={titles}
                onChange={(next, post) => {
                  if (post) {
                    setTitles((current) => ({ ...current, [post.postId]: post.title }));
                  }
                  write({ postIds: next });
                }}
              />
              <Button type="submit">Save</Button>
            </Form>
          </Col>
        </Row>
      </Container>
    </CancellableContent>
  );
};
